/**
 * Generate Assignments Modal
 * Modal wrapper for generating on-call assignments
 */

import React, { useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { Dialog, Button, FormItem, Typogaphy } from 'vnetwork-ui'
import { RotateCw } from 'lucide-react'

import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import datacentral from '@/utils/axios/datacentral'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface GenerateAssignmentsPayload {
    schedule_id: number
    start_date: string
    end_date: string
}

interface FormData {
    schedule_id: string
    start_date: string
    end_date: string
}

interface OncallScheduleResponse {
    oncall_schedule_id: number
    oncall_schedule_name: string
    oncall_schedule_active: boolean
    department_name: string
    rotation_count?: number
}

export interface GenerateAssignmentsModalProps {
    open: boolean
    onClose: () => void
    refetch?: () => void
    scheduleId?: number // Pre-select schedule when provided
}

const getOncallSchedules = async (payload: {
    page: number
    page_size: number
}) => {
    const response = await datacentral.get(
        `/oncall/schedules?page=${payload.page}&page_size=${payload.page_size}`
    )
    return response.data
}

const generateOncallAssignments = async (
    payload: GenerateAssignmentsPayload
) => {
    const response = await datacentral.post(
        '/oncall/assignments/generate',
        payload
    )
    return response.data
}

const generateSchema = yup.object().shape({
    schedule_id: yup.string().required('schedule_required'),
    start_date: yup.string().required('start_date_required'),
    end_date: yup
        .string()
        .required('end_date_required')
        .test(
            'is-after-start',
            'end_date_must_be_after_start_date',
            function (value) {
                const { start_date } = this.parent
                if (!start_date || !value) return true
                return dayjs(value).isSameOrAfter(dayjs(start_date))
            }
        ),
})

const GenerateAssignmentsModal: React.FC<GenerateAssignmentsModalProps> = ({
    open,
    onClose,
    refetch,
    scheduleId,
}) => {
    const { t: translate } = useTranslation('oncall')

    // Fetch schedules for dropdown
    const { data: schedulesData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ONCALL_SCHEDULES, 'all'],
        queryFn: () => getOncallSchedules({ page: 1, page_size: 100 }),
    })

    const schedules: OncallScheduleResponse[] = (
        schedulesData?.results ||
        schedulesData?.result ||
        []
    ).filter((s: OncallScheduleResponse) => s.oncall_schedule_active)

    const scheduleOptions = schedules.map((s: OncallScheduleResponse) => ({
        label: s.oncall_schedule_name,
        value: String(s.oncall_schedule_id),
    }))

    const {
        control,
        handleSubmit,
        formState: { isSubmitting },
        reset,
    } = useForm<FormData>({
        mode: 'onSubmit',
        defaultValues: {
            schedule_id: scheduleId ? String(scheduleId) : '',
            start_date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
            end_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
        },
        resolver: yupResolver(generateSchema),
    })

    // Watch selected schedule to show details
    const selectedScheduleId = useWatch({ control, name: 'schedule_id' })

    const selectedSchedule = useMemo(() => {
        if (!selectedScheduleId) return null
        return schedules.find(
            (s) => String(s.oncall_schedule_id) === selectedScheduleId
        )
    }, [selectedScheduleId, schedules])

    const { mutateAsync: doGenerate, isPending: isGenerating } =
        useMutationWithHandler(
            {
                mutationFn: generateOncallAssignments,
            },
            {
                reactQueryKeys: QUERY_KEYS.ONCALL_ASSIGNMENTS,
                errorMessage: 'Failed to generate assignments',
            }
        )

    const handleGenerate = async (formData: FormData) => {
        try {
            const payload: GenerateAssignmentsPayload = {
                schedule_id: Number(formData.schedule_id),
                start_date: dayjs(formData.start_date).format('YYYY-MM-DD'),
                end_date: dayjs(formData.end_date).format('YYYY-MM-DD'),
            }
            const result = await doGenerate(payload)
            if (result && 'result' in result) {
                const generated = result.result as {
                    assignments_created?: number
                }
                toast.success(
                    translate('assignments_generated_success', {
                        count: generated?.assignments_created || 0,
                    })
                )
            }
            reset()
            refetch?.()
            onClose()
        } catch (error) {
            console.error(error)
        }
    }

    const handleClose = () => {
        reset()
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={translate('generate_assignments')}
            maxWidth="sm"
        >
            <Typogaphy size="small" className="text-gray-600 mb-4">
                {translate('generate_assignments_description')}
            </Typogaphy>

            <div className="space-y-4">
                <FormItem
                    control={control}
                    name="schedule_id"
                    type="select"
                    label={`${translate('schedule')} *`}
                    placeholder={translate('select_schedule')}
                    options={scheduleOptions}
                />

                {/* Schedule Details Panel */}
                {selectedSchedule && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2">
                            <Typogaphy
                                size="small"
                                className="font-semibold text-gray-800"
                            >
                                {selectedSchedule.oncall_schedule_name}
                            </Typogaphy>
                            {selectedSchedule.department_name && (
                                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                                    {selectedSchedule.department_name}
                                </span>
                            )}
                            <div className="flex items-center gap-1 ml-auto">
                                <RotateCw className="w-3 h-3 text-green-500" />
                                <span className="text-xs text-gray-600">
                                    {selectedSchedule.rotation_count || 0}{' '}
                                    {translate(
                                        'rotations_configured',
                                        'rotations'
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <FormItem
                        control={control}
                        name="start_date"
                        type="datepicker"
                        label={`${translate('start_date')} *`}
                    />

                    <FormItem
                        control={control}
                        name="end_date"
                        type="datepicker"
                        label={`${translate('end_date')} *`}
                    />
                </div>

                <div className="bg-blue-50 p-3 rounded-lg">
                    <Typogaphy size="small" className="text-blue-700">
                        {translate('generate_assignments_note')}
                    </Typogaphy>
                </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
                <Button variant="outlined" onClick={handleClose}>
                    {translate('cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleGenerate)}
                    loading={isGenerating}
                    disabled={isSubmitting || isGenerating}
                >
                    {translate('generate')}
                </Button>
            </div>
        </Dialog>
    )
}

export default GenerateAssignmentsModal
